import type { ReactNode } from "react";

import { useModal } from "../lib/useModal";
import { useCredits } from "../api/hooks";

function FundingIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16">
      <rect x="1" y="1" width="6" height="6" fill="currentColor" />
      <rect x="9" y="1" width="6" height="6" fill="currentColor" opacity=".5" />
      <rect x="1" y="9" width="6" height="6" fill="currentColor" opacity=".5" />
      <rect x="9" y="9" width="6" height="6" fill="currentColor" />
    </svg>
  );
}

function TechnologyIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16">
      <circle cx="8" cy="8" r="3" fill="currentColor" />
      <circle cx="2" cy="3" r="1.6" fill="currentColor" opacity=".6" />
      <circle cx="14" cy="3" r="1.6" fill="currentColor" opacity=".6" />
      <circle cx="2" cy="13" r="1.6" fill="currentColor" opacity=".6" />
      <circle cx="14" cy="13" r="1.6" fill="currentColor" opacity=".6" />
      <line x1="8" y1="8" x2="2" y2="3" stroke="currentColor" strokeWidth="1" />
      <line x1="8" y1="8" x2="14" y2="3" stroke="currentColor" strokeWidth="1" />
      <line x1="8" y1="8" x2="2" y2="13" stroke="currentColor" strokeWidth="1" />
      <line x1="8" y1="8" x2="14" y2="13" stroke="currentColor" strokeWidth="1" />
    </svg>
  );
}

interface Group {
  key: "f" | "t";
  label: string;
  icon: ReactNode;
  parentView: string;
}

const GROUPS: Group[] = [
  { key: "f", label: "Förderungen", icon: <FundingIcon />, parentView: "foerderungen" },
  { key: "t", label: "Technologie", icon: <TechnologyIcon />, parentView: "technologie" },
];

export function Sidebar({
  view,
  open,
  compact,
  onClose,
  navOpen,
  onToggleGroup,
}: {
  view: string;
  open: boolean;
  compact: boolean;
  onClose: () => void;
  navOpen: Record<"f" | "t", boolean>;
  onToggleGroup: (key: "f" | "t") => void;
}) {
  const modal = useModal(compact && open, onClose);

  return (
    <div id="sidebar" ref={modal} role={compact && open ? "dialog" : undefined} aria-modal={compact && open ? true : undefined} aria-label="Research-Agenten" tabIndex={-1} inert={compact && !open} className={open ? "open" : ""}>
      {compact && <button className="sidebar-close icon-btn" aria-label="Navigation schließen" onClick={onClose}>×</button>}
      <nav aria-label="Research-Agenten">
      <div className="nav-label">Research-Agenten</div>
      {GROUPS.map((group) => (
        <div className="agent-block" data-group={group.key} key={group.key}>
          <div
            className={
              "navitem parent" +
              (view === group.parentView ? " active" : "") +
              (navOpen[group.key] ? " open" : "")
            }
            data-view={group.parentView}
          >
            <a href={`#/${group.parentView}`} onClick={onClose} aria-current={view === group.parentView ? "page" : undefined}>{group.icon}{group.label}</a>
            <button
              className="caret"
              aria-label="Unterpunkte ein- oder ausklappen"
              onClick={(event) => {
                event.stopPropagation();
                onToggleGroup(group.key);
              }}
            >
              ▸
            </button>
          </div>
          <a
            className={
              "navitem sub" +
              (navOpen[group.key] ? " shown" : "") +
              (view === `schaerfen-${group.key}` ? " active" : "")
            }
            href={`#/schaerfen-${group.key}`} onClick={onClose}
          >
            Suchauftrag
          </a>
          <a
            className={
              "navitem sub" +
              (navOpen[group.key] ? " shown" : "") +
              (view === `zustand-${group.key}` ? " active" : "")
            }
            href={`#/zustand-${group.key}`} onClick={onClose}
          >
            Historie
          </a>
        </div>
      ))}
      <div
        className="nav-add"
        aria-disabled="true"
        title="Weiteren Research-Agenten anlegen (bald verfügbar)"
      >
        <span className="plus">+</span>Research-Agent
      </div>

      </nav>
      <CreditMeter />
    </div>
  );
}

/**
 * What is left of the month, where the disabled "new agent" button used to
 * sit. One credit is one euro; model calls and web searches both count.
 */
function CreditMeter() {
  const credits = useCredits();
  if (!credits.data) return null;

  const { limit, spent, remaining } = credits.data;
  const used = limit > 0 ? Math.min(100, (spent / limit) * 100) : 0;
  const low = remaining <= limit * 0.15;

  return (
    <div className="credit-meter" title={`${spent.toFixed(2)} von ${limit.toFixed(2)} verbraucht`}>
      <div className="credit-head">
        <span>Credits</span>
      </div>
      <div className="credit-bar">
        <div className={"credit-fill" + (low ? " low" : "")} style={{ width: `${used}%` }} />
      </div>
      <div className="credit-foot">
        {spent.toFixed(2)} / {limit.toFixed(0)} pro Monat
      </div>
    </div>
  );
}
